import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { apiErr } from "../lib/api";
import { Logo, LanguageSwitcher } from "../components/Common";
import { Loader2, ShieldAlert } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1652120268427-9ba66e870467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjBkYXRhJTIwd2F2ZXN8ZW58MHx8fHwxNzg2NzA5NDk1fDA&ixlib=rb-4.1.0&q=85";

export function AuthShell({ children, title }) {
  const { t } = useLang();
  return (
    <div className="min-h-screen relative flex items-center justify-center px-4">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Logo />
          <LanguageSwitcher />
        </div>
        <div className="border border-[#1E2028] bg-[#0F1015] p-8">
          <h1 className="font-display text-2xl font-bold mb-1">{title}</h1>
          <p className="text-xs text-[#9CA3AF] mb-6 font-mono">{t("tagline")}</p>
          {children}
        </div>
        <div className="mt-4 flex items-start gap-2 text-[10px] text-[#6B7280] leading-relaxed">
          <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#F59E0B]" />
          <span>{t("disclaimer")}</span>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  const demo = async () => {
    setBusy(true);
    try { await login("admin@aurora.ai", "Aurora2026!"); nav("/"); }
    catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title={t("login")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={t("email")} type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field label={t("password")} type="password" value={password} onChange={setPassword} testid="login-password" />
        <button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full bg-[#FACC15] text-[#05050A] font-semibold py-2.5 text-sm hover:bg-[#eab308] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("signIn")}
        </button>
      </form>
      <button onClick={demo} disabled={busy} data-testid="demo-login"
        className="w-full mt-3 border border-[#1E2028] text-[#9CA3AF] py-2 text-xs font-mono hover:border-[#4B5563] hover:text-white transition-colors duration-200">
        {t("demoLogin")} · admin@aurora.ai
      </button>
      <p className="mt-6 text-center text-xs text-[#6B7280]">
        {t("noAccount")}{" "}
        <Link to="/register" className="text-[#FACC15] hover:underline" data-testid="go-register">{t("signUp")}</Link>
      </p>
    </AuthShell>
  );
}

export function Field({ label, type, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#9CA3AF] font-mono">{label}</span>
      <input
        type={type} value={value} required data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-[#05050A] border border-[#1E2028] px-3 py-2.5 text-sm text-white focus:border-[#FACC15] focus:outline-none transition-colors duration-200"
      />
    </label>
  );
}
